import { Pencil, SquareStack, X } from 'lucide-react';
import type { FieldMeta, ResourceMeta } from '../../types/resource';
import type { SubmissionRow } from '../../types/submission';
import { formatFieldValue } from '../../utils/fieldValue';
import { useSubmission } from '../../hooks/useSubmission';
import { ChildrenPanel } from './ChildrenPanel';

interface ResourceDetailDrawerProps {
  meta: ResourceMeta;
  parentResource: string;
  row: SubmissionRow;
  onClose: () => void;
  onEdit?: (row: SubmissionRow) => void;
}

export function ResourceDetailDrawer({
  meta,
  parentResource,
  row,
  onClose,
  onEdit,
}: ResourceDetailDrawerProps) {
  // Only re-fetch when we came from a summary-shape row (no updated_at).
  const needsRefetch = row.updated_at === null || row.updated_at === undefined;
  const { row: freshRow, loading: rowLoading } = useSubmission(
    needsRefetch ? parentResource : undefined,
    needsRefetch ? row.id : undefined,
  );
  const displayRow = freshRow ?? row;

  const summaryView = meta.view_meta?.summary_view;
  const primary =
    (summaryView?.title && pickString(displayRow, summaryView.title)) ||
    (summaryView?.id && pickString(displayRow, summaryView.id)) ||
    String(displayRow.id ?? '—');
  const subtitle =
    summaryView?.subtitle && pickString(displayRow, summaryView.subtitle);
  const summaryChips = (summaryView?.summary ?? [])
    .map((key) => pickString(displayRow, key))
    .filter((v): v is string => Boolean(v && v.trim()));

  const detailFields = detailFieldsOf(meta);
  const children = meta.children ?? [];

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-labelledby="detail-drawer-title"
        className="fixed inset-y-0 right-0 z-40 flex w-full max-w-5xl flex-col bg-white shadow-2xl"
      >
        <header className="flex items-start justify-between gap-4 border-b border-rgf-gray-200 px-6 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-rgf-gray-500">
              <SquareStack className="h-3.5 w-3.5" />
              Record details
            </div>
            <h2
              id="detail-drawer-title"
              className="mt-1 truncate text-lg font-semibold text-rgf-gray-900"
            >
              {primary}
            </h2>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-rgf-gray-500">
              <span>
                {meta.display_title || parentResource} · id {String(row.id ?? '—')}
              </span>
              {subtitle && <span>· {subtitle}</span>}
              {summaryChips.length > 0 && (
                <span>· {summaryChips.join(' · ')}</span>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(displayRow)}
                disabled={rowLoading}
                className="inline-flex items-center gap-1 rounded-md border border-rgf-primary-200 bg-rgf-primary-50 px-3 py-1.5 text-xs font-medium text-rgf-primary-700 hover:bg-rgf-primary-100 disabled:opacity-50 disabled:cursor-not-allowed"
                title={rowLoading ? 'Loading full record…' : 'Edit record'}
              >
                <Pencil className="h-3.5 w-3.5" />
                {rowLoading ? 'Loading…' : 'Edit'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="rounded-md p-1.5 text-rgf-gray-500 hover:bg-rgf-gray-100 hover:text-rgf-gray-900"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto bg-rgf-gray-50/40">
          <div className="space-y-6 px-6 py-5">
            <DetailsSection fields={detailFields} row={displayRow} />
            {children.length > 0 && (
              <SubRecordsSection>
                <ChildrenPanel
                  parentResource={parentResource}
                  parentRowId={row.id}
                  children={children}
                />
              </SubRecordsSection>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

function DetailsSection({
  fields,
  row,
}: {
  fields: FieldMeta[];
  row: SubmissionRow;
}) {
  if (fields.length === 0) {
    return (
      <SectionShell heading="Details">
        <div className="rounded-lg border border-dashed border-rgf-gray-200 bg-white p-6 text-center text-sm text-rgf-gray-500">
          No fields declared on this resource.
        </div>
      </SectionShell>
    );
  }
  return (
    <SectionShell heading="Details">
      <div className="rounded-xl border border-rgf-gray-200 bg-white p-4 shadow-sm">
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          {fields.map((field) => (
            <div key={field.title} className="min-w-0">
              <dt className="text-[11px] font-medium uppercase tracking-wider text-rgf-gray-500">
                {field.display_title || field.title}
              </dt>
              <dd className="mt-0.5 truncate text-sm text-rgf-gray-900">
                {formatFieldValue(field, row[field.title])}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </SectionShell>
  );
}

function SubRecordsSection({ children }: { children: React.ReactNode }) {
  return <SectionShell heading="Sub-records">{children}</SectionShell>;
}

function SectionShell({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wider text-rgf-gray-500">
        {heading}
      </h3>
      {children}
    </section>
  );
}

function detailFieldsOf(meta: ResourceMeta): FieldMeta[] {
  return (meta.view_meta?.fields ?? []).filter((f) => f.list_visible !== false);
}

function pickString(row: SubmissionRow, key: string | undefined): string | undefined {
  if (!key) return undefined;
  const raw = row[key];
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (typeof raw === 'string' || typeof raw === 'number' || typeof raw === 'boolean') {
    return String(raw);
  }
  if (typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    if (typeof obj.name === 'string') return obj.name;
    if (typeof obj.title === 'string') return obj.title;
  }
  return undefined;
}
